// filepath: src/apps/policymaker/pages/ICR/page/IcrPage.jsx
import React from 'react';
import {Button, Divider, Stack, Typography} from '@mui/material';
import {useNavigate, useParams} from 'react-router';
import {useIcr} from '../../../../../firebase/firestore/useIcr';

/**
 * IcrPage - displays a single Information Collection Request document and related details.
 * Route: /icr/:icrId
 */
const IcrPage = () => {
    const {icrId} = useParams();
    const navigate = useNavigate();

    const {data, loading, error} = useIcr(icrId);

    if (loading) return <Stack p={2}>Loading ICR…</Stack>;
    if (error) return <Stack p={2} sx={{color: 'red'}}>Error loading ICR: {String(error?.message || error)}</Stack>;
    if (!data) return <Stack p={2}>ICR not found.</Stack>;

    return (
        <Stack>
            <Stack p={1} bgcolor={'white'} direction={'row'}>
                <Button onClick={() => navigate('/icr')}>Back</Button>
            </Stack>

            <Stack direction={'row'} p={2} spacing={2} alignItems={'flex-start'}>
                <Stack p={2} borderRadius={2} bgcolor={'white'} spacing={2} flex={1}>
                    <Stack spacing={1}>
                        <Typography variant={'h5'}>{data.id || data.icrId || data.type}</Typography>
                        <Typography variant={'subtitle1'}>{data.tasking?.summary}</Typography>
                    </Stack>

                    <Stack spacing={1}>
                        <Typography variant={'body2'} color={'text.secondary'}>Tasking Details:</Typography>
                        <Typography
                            sx={{whiteSpace: 'pre-line'}}>{data.tasking?.justification || data.tasking?.summary}</Typography>
                    </Stack>

                    <Divider/>

                    <Stack>
                        <Typography variant={'h6'}>Linked Requirements</Typography>
                        <Typography>IR: {data.linked_requirements?.ir_id || 'N/A'}</Typography>
                        <Typography>PIR: {data.linked_requirements?.pir_id || 'N/A'}</Typography>
                    </Stack>

                    <Divider/>

                    <Stack>
                        <Typography variant={'h6'}>Indicators</Typography>
                        {data.tasking?.indicators && data.tasking.indicators.length > 0 ? (
                            data.tasking.indicators.map((ind, i) => (
                                <Stack key={i} sx={{mt: 1}}>
                                    <Typography variant={'subtitle2'}>{ind.indicator_id}</Typography>
                                    <Typography>{ind.description}</Typography>
                                </Stack>
                            ))
                        ) : (
                            <Typography>No indicators specified.</Typography>
                        )}
                    </Stack>

                    <Divider/>

                    <Stack>
                        <Typography variant={'h6'}>Constraints</Typography>
                        <Typography variant={'subtitle2'}>Legal</Typography>
                        {data.constraints?.legal && data.constraints.legal.length > 0 ? (
                            data.constraints.legal.map((l, i) => <Typography key={i}>- {l}</Typography>)
                        ) : <Typography>N/A</Typography>}

                        <Typography variant={'subtitle2'} sx={{mt: 1}}>Technical</Typography>
                        {data.constraints?.technical && data.constraints.technical.length > 0 ? (
                            data.constraints.technical.map((t, i) => <Typography key={i}>- {t}</Typography>)
                        ) : <Typography>N/A</Typography>}
                    </Stack>
                </Stack>

                <Stack p={2} borderRadius={2} bgcolor={'white'} width={320} spacing={2}>
                    <Typography variant={'h6'}>Metadata</Typography>
                    <Stack spacing={1}>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Requester</Typography>
                            <Typography>{data.requester?.agency || 'N/A'}</Typography>
                            <Typography variant={'body2'}
                                        color={'text.secondary'}>{data.requester?.office || ''}</Typography>
                            <Typography variant={'body2'}
                                        color={'text.secondary'}>{data.requester?.poc || ''}</Typography>
                        </Stack>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Status</Typography>
                            <Typography>{data.status?.state || 'N/A'}</Typography>
                        </Stack>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Created</Typography>
                            <Typography>{data.status?.created || 'N/A'}</Typography>
                        </Stack>
                        <Stack>
                            <Typography variant={'body2'} color={'text.secondary'}>Last updated</Typography>
                            <Typography>{data.status?.last_updated || 'N/A'}</Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default IcrPage;

